<template>
  <v-content class="pa-0 ma-0">
    <preview-document-dialog
      v-if="selectedDocument"
      :opened.sync="previewDocumentDialogOpened"
      :documentPreviewData="documentPreviewData"
      :documentType="selectedDocument.type"
      :selectedCompanyId="
        selectedQuestionnaires && selectedQuestionnaires[0]
          ? selectedQuestionnaires[0].company_id
          : null
      "
      :selectedDocumentLink="selectedDocument.source"
      :previewType="previewType"
      :previewLoaded="previewLoaded"
      :hideMoveButton="true"
      :hideRotateButton="true"
      :questionnaireId="
        selectedQuestionnaires && selectedQuestionnaires[0]
          ? selectedQuestionnaires[0].id
          : undefined
      "
      @on-pdf-saved="
        previewDocumentDialogOpened = false;
        refresh();
      "
    />

    <v-dialog
      v-model="dialogOpened"
      width="800"
      persistent
      scrollable
      @keydown.esc="dialogOpened = false"
    >
      <v-card block flat v-if="selectedQuestionnaires[0]">
        <v-card-title class="title pb-0">
          <span v-if="selectedQuestionnaires.length === 1">
            Export {{ selectedQuestionnaires[0].formatted_name }}
          </span>
          <span v-else
            >Export {{ selectedQuestionnaires[0].name }} and
            {{ selectedQuestionnaires.length - 1 }} other{{
              selectedQuestionnaires.length > 2 ? 's' : ''
            }}
          </span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-layout class="text-xs-left mb-4">
            <v-flex xs12>
              <div>Export Format<span class="error--text">*</span></div>
              <v-select
                class="default-input body-1"
                hide-details
                solo
                :items="exportFormats"
                v-model="selectedFormat"
                placeholder="Choose a format"
              />
            </v-flex>
          </v-layout>
          <v-layout
            class="mb-4 text-xs-left"
            align-start
            v-if="
              selectedFormat === 'PDF' || selectedFormat === 'Zipped Folder'
            "
          >
            <v-flex xs12>
              Options
              <v-checkbox
                v-model="includeTOC"
                class="ma-0 pa-0 shrink"
                hide-details
                label="Include Table of Contents"
              />
              <v-checkbox
                v-if="includeTOC"
                v-model="includePages"
                class="ma-0 pa-0 shrink"
                hide-details
                label="Include Page Numbers"
              />
              <v-checkbox
                v-if="selectedFormat !== 'Zipped Folder'"
                v-model="includeDocuments"
                class="ma-0 pa-0 shrink"
                hide-details
                label="Include Documents"
              />
              <v-checkbox
                v-model="removeNosAndNaAnswers"
                class="ma-0 pa-0 shrink"
                hide-details
                label="Remove all No's and N/A's"
              />
              <v-checkbox
                v-if="selectedFormat === 'Zipped Folder'"
                v-model="convertImagesToPdfs"
                class="ma-0 pa-0 shrink"
                hide-details
                label="Convert all images to PDFs"
              />
              <v-checkbox
                v-if="
                  currentFirm.preferences.export_to_third_party_dms_enabled &&
                    (selectedFormat === 'PDF' ||
                      selectedFormat === 'Zipped Folder')
                "
                v-for="platform in selectablePlatforms"
                :key="platform.id"
                v-model="platform.selected"
                class="ma-0 pa-0 shrink"
                hide-details
              >
                <template slot="label">
                  {{ `Export to ${platform.name}` }}
                </template>
              </v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout
            class="mb-4 text-xs-left"
            align-start
            v-if="selectedQuestionnaires.length === 1"
          >
            <v-flex xs12>
              <div>Save as<span class="error--text">*</span></div>
              <v-layout align-center>
                <v-text-field
                  class="default-input body-1 mr-2 ph-no-capture"
                  hide-details
                  solo
                  v-model="fileNameToSave"
                  placeholder="Required"
                />
                <span v-if="selectedFormat === 'PDF'">.pdf</span>
                <span v-else-if="selectedFormat === 'Zipped Folder'">.zip</span>
                <span v-else-if="selectedFormat === 'Spreadsheet'">.xls</span>
              </v-layout>
            </v-flex>
          </v-layout>

          <v-layout
            v-if="selectedQuestionnaires.length === 1 && displayDocumentSection"
          >
            <v-flex xs12 class="text-xs-left">
              <v-layout>
                <div class="mb-2">Documents</div>
                <v-spacer />
                <v-tooltip bottom
                  ><span slot="activator" class="grey--text text--darken-1"
                    >What will be included in the PDF export?</span
                  ><span
                    >PDFs and most image formats will be shown in the PDF
                    export. All other formats will be linked.</span
                  ></v-tooltip
                >
              </v-layout>

              <v-layout column>
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  size="25"
                  color="primary"
                />

                <draggable
                  v-else-if="newQuestionnaireExportEnabled"
                  :list="exportableDocuments"
                  class="list-group"
                  ghost-class="ghost"
                  @start="dragging = true"
                  @end="dragging = false"
                >
                  <v-layout
                    justify-start
                    align-center
                    class="document_item"
                    v-for="(doc, i) in exportableDocuments"
                    :key="doc.id"
                  >
                    <v-icon
                      class="mr-2 grey--text"
                      style="cursor: pointer"
                      small
                    >
                      drag_indicator
                    </v-icon>

                    <div>
                      <v-checkbox
                        class="mt-0 pt-0"
                        hide-details
                        :input-value="!excluded.includes(doc.id)"
                        @change="toggleExclude(doc.id)"
                      />
                    </div>

                    <v-layout class="text-xs-left" align-center>
                      <v-layout align-center v-if="doc.editingDescription">
                        <v-text-field
                          v-model="doc.newDescription"
                          hide-details
                          solo
                          class="default-input"
                        />

                        <v-layout>
                          <v-btn
                            @click="saveNewDescription(doc)"
                            color="success"
                            class="text-none"
                            small
                            icon
                            flat
                          >
                            <v-icon small>check</v-icon>
                          </v-btn>

                          <v-btn
                            @click="cancelEditingDescription(doc)"
                            color="red"
                            class="text-none"
                            small
                            icon
                            flat
                          >
                            <v-icon small>close</v-icon>
                          </v-btn>
                        </v-layout>
                      </v-layout>

                      <v-layout v-else align-center>
                        <div
                          class="document_item_name"
                          @click.stop.prevent="previewDocument(doc)"
                        >
                          {{ getDocumentName(doc.source) }}
                        </div>
                        <v-chip v-if="doc.description">
                          {{ doc.description }}
                        </v-chip>
                      </v-layout>

                      <span v-if="!doc.id" class="pl-1" style="color: red">
                        (Document not found)
                      </span>

                      <v-tooltip v-else-if="!doc.editingDescription" bottom>
                        <v-btn
                          slot="activator"
                          icon
                          flat
                          small
                          @click="startEditDocDescription(doc)"
                        >
                          <v-icon small color="primary" class="edit-icon"
                            >edit</v-icon
                          >
                        </v-btn>
                        <span>Edit Description</span>
                      </v-tooltip>
                    </v-layout>

                    <v-tooltip v-if="doc.exported_at" top>
                      <v-chip slot="activator" small>
                        <v-icon small color="green" class="mr-1"
                          >check_circle</v-icon
                        >
                        Exported
                      </v-chip>
                      <span>
                        Exported at
                        {{ moment(doc.exported_at, 'YYYY-MM-DD HH:mm') }}
                      </span>
                    </v-tooltip>
                  </v-layout>
                </draggable>

                <MultipleOptionsInput
                  v-else
                  v-model="exportableDocuments"
                  :excluded.sync="excluded"
                  hint="Note: Renaming document here does not change file name in Soraban Documents page."
                >
                  <template v-slot:label="{ option }">
                    <v-layout
                      align-center
                      wrap
                      @click.stop.prevent="previewDocument(option)"
                    >
                      <v-tooltip top>
                        <v-layout align-center slot="activator">
                          <span slot="activator" class="body-1 dark--text">
                            {{ getDocumentName(option.source) }}
                          </span>

                          <v-chip v-if="option.detailed_name" small>
                            {{ option.detailed_name }}
                          </v-chip>
                        </v-layout>
                        <span
                          >Uploaded at
                          {{ moment(option.created_at).format('lll') }}</span
                        >
                      </v-tooltip>

                      <v-tooltip v-if="option.exported_at" top>
                        <v-chip slot="activator" small>
                          <v-icon small color="green" class="mr-1"
                            >check</v-icon
                          >
                          Exported
                        </v-chip>
                        <span>
                          Exported at
                          {{ moment(option.exported_at, 'YYYY-MM-DD HH:mm') }}
                        </span>
                      </v-tooltip>
                    </v-layout>
                  </template>
                </MultipleOptionsInput>
              </v-layout>

              <v-layout
                align-center
                v-if="!loading && isEmpty(exportableDocuments)"
                class="darkgrey--text"
              >
                None were uploaded.
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="exportQuestionnaires()"
            :disabled="!questionnairesExportable || loading"
          >
            Export
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="dialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-content>
</template>

<script>
import API from '@/api';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import MultipleOptionsInput from '../pages/accountant/MultipleOptionsInput';
import PreviewDocumentDialog from '../components/dialogs/PreviewDocumentDialog';
import { mapGetters } from 'vuex';
import {
  supportedExtensionsForPreview,
  usTaxForms,
} from '@/constants/variables';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import UtilsMixins from '../mixins/UtilsMixins';
import draggable from 'vuedraggable';

export default {
  name: 'ExportQuestionnaireDialog',
  mixins: [FirmPreferenceMixins, UtilsMixins],
  components: {
    MultipleOptionsInput,
    PreviewDocumentDialog,
    draggable,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentCompany: {
      type: Object,
      required: false,
      default: null,
    },
    message: {
      type: String,
      default: '',
    },
    selectedQuestionnaires: {
      type: Array,
      default: () => [],
    },
    allQuestionnaires: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      dialogOpened: false,
      selectedQuestionnaire: null,
      includeDocuments: true,
      includeTOC: false,
      removeNosAndNaAnswers: false,
      convertImagesToPdfs: true,
      selectedFormat: null,
      documents: [],
      exportableDocuments: [],
      exportFormats: ['Spreadsheet', 'PDF', 'Zipped Folder'],
      fileNameToSave: '',
      // NOTE @chris Record<typeof QuestionnaireId, string>
      fileNamesToSave: {},
      documentHeaders: [
        {
          text: 'Name (Click to preview or download)',
          align: 'left',
          sortable: true,
          value: 'name',
        },
      ],
      exportTask: null,
      previewDocumentDialogOpened: false,
      previewType: '',
      previewLoaded: false,
      documentPreviewData: null,
      selectedDocument: '',
      supportedExtensionsForPreview,
      excluded: [],
      selectablePlatforms: [],
    };
  },
  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),
    exportToPlatforms() {
      return this.selectablePlatforms
        .filter(platform => platform.selected)
        .map(platform => platform.name);
    },

    questionnairesExportable() {
      if (isEmpty(this.selectedFormat)) return false;
      if (isEmpty(this.fileNameToSave)) return false;

      const hasSelectedQuestionnaire = Boolean(
        this.selectedQuestionnaires.length >= 1,
      );

      return hasSelectedQuestionnaire;
    },

    displayDocumentSection() {
      return (
        this.selectedFormat === 'Zipped Folder' ||
        this.selectedFormat === 'Scan and AutoFlow' ||
        (this.includeDocuments && ['PDF'].includes(this.selectedFormat))
      );
    },
  },
  methods: {
    isEmpty,
    moment,
    getFileNameToSave() {
      const firstQuestionnaire =
        this.selectedQuestionnaires && this.selectedQuestionnaires[0];
      const fileNameToSave = this.getFileNameFromQuestionnaire(
        firstQuestionnaire,
      );

      return fileNameToSave;
    },
    getFileNameFromQuestionnaire(questionnaire) {
      if (!questionnaire) return;

      const company = questionnaire.company || this.currentCompany;
      const hasCompany = Boolean(company);
      const isIndividual = hasCompany && company.entity_type === 'Individual';
      const hasMoreCompanies = Boolean(
        this.currentUser.owned_company_ids.length > 1,
      );

      // prettier-ignore
      const taxYear = questionnaire ?
				` (${questionnaire.tax_year})` :
				null;

      const getName = () => {
        if (!hasCompany && questionnaire) return questionnaire.client;
        if (hasMoreCompanies) return company.tax_return_name;
        if (isIndividual) return company.user.tax_return_name;

        return `${company.name} (${company.user.full_name})`;
      };

      let fileNameToSave = getName();

      // Attach questionnaire name
      if (questionnaire) fileNameToSave += ` - ${questionnaire.name}`;

      // Attach tax year
      if (taxYear) fileNameToSave += taxYear;

      return fileNameToSave;
    },
    async getExportTask() {
      const response = await API.exportTask.getExportTaskByQuestionnaireId({
        questionnaireId: this.selectedQuestionnaires[0].id,
        companyId: this.currentCompany.id,
      });

      this.exportTask = response.data;
    },

    async exportQuestionnaires() {
      if (this.selectedFormat === 'PDF') {
        this.exportToPDF(this.selectedQuestionnaires);
      } else if (this.selectedFormat === 'Spreadsheet') {
        this.exportToSpreadsheet(this.selectedQuestionnaires);
      } else if (this.selectedFormat === 'Zipped Folder') {
        this.exportToZippedFolder(this.selectedQuestionnaires);
      } else if (this.selectedFormat === 'Scan and AutoFlow') {
        this.exportToScanAndAutoFlow(this.selectedQuestionnaires);
      }
    },

    async loadQuestionnaire(questionnaire, company_id) {
      try {
        this.loading = true;
        const response = await API.questionnaire.getQuestionnaire({
          companyId: company_id,
          id: questionnaire.id,
        });
        this.selectedQuestionnaire = response.data;

        // Include unattached documents from `related_document`
        const unattachedDocuments = this.selectedQuestionnaire.related_documents.map(
          document => {
            const documentSegments = document.split('/');
            const documentName = documentSegments[documentSegments.length - 1];

            return {
              id: null,
              name: documentName,
              source: document,
            };
          },
        );

        const allDocuments = [
          ...this.selectedQuestionnaire.extracted_documents,
          ...unattachedDocuments,
        ];

        // Sort the documents based on the order of the sections they belong to
        this.documents = sortBy(allDocuments, d => {
          const q = this.selectedQuestionnaire.questionnaire_questions.find(
            q => {
              if (q.question_type !== 'upload' || isEmpty(q.answer)) {
                return false;
              }

              return (
                // Return the question that contains this document
                typeof q.answer
                  .split('&!&')
                  .filter(answer => !isEmpty(answer))
                  .find(answer => {
                    return d.source.includes(answer);
                  }) !== 'undefined'
              );
            },
          );

          const lastPosition = this.selectedQuestionnaire.sections.length;

          if (q) {
            // Find the section that contains the question
            const section = this.selectedQuestionnaire.sections.find(
              section => section.id === q.section_id,
            );

            return section ? section.order : lastPosition;
          } else {
            return lastPosition;
          }
        });

        this.loading = false;
      } catch (e) {
        console.log(e);
      }
    },
    getDocumentName(document) {
      return document.split('/')[document.split('/').length - 1];
    },

    async getQuestionnaire(id, company_id) {
      const response = await API.questionnaire.getQuestionnaire({
        id,
        companyId: company_id,
      });

      return response.data;
    },

    async exportToPDF(questionnaires) {
      try {
        const response1 = await API.questionnaire.exportQuestionnaires({
          questionnaireIds: questionnaires.map(q => q.id),
          includeTOC: this.includeTOC,
          includePages: this.includeTOC && this.includePages,
          includeDocuments: this.includeDocuments,
          removeNosAndNaAnswers: this.removeNosAndNaAnswers,

          saveAsName: this.fileNameToSave,
          saveAsNames: this.fileNamesToSave,
          documentsToInclude: this.exportableDocuments
            .filter(doc => !this.excluded.includes(doc.id))
            .map(doc => ({
              id: doc.id,
              path: doc.source,
              name: doc.name,
            })),
          documentsToExclude: this.exportableDocuments
            .filter(doc => this.excluded.includes(doc.id))
            .map(doc => ({
              id: doc.id,
              path: doc.source,
              name: doc.name,
            })),
          triggerWebhook: this.exportToPlatforms.length > 0,
          exportToPlatforms: this.exportToPlatforms,
        });

        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              "Export queued! You will get a notifications once they're ready to be downloaded.",
          });
          this.dialogOpened = false;
          if (
            this.selectedQuestionnaires.length === 1 &&
            !this.selectedQuestionnaires[0].locked
          ) {
            this.$emit('open-lock-questionnaire-confirmation-dialog');
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    async exportToZippedFolder(questionnaires) {
      try {
        const response1 = await API.questionnaire.exportQuestionnaires({
          includeTOC: this.includeTOC,
          includePages: this.includeTOC && this.includePages,
          questionnaireIds: questionnaires.map(q => q.id),
          removeNosAndNaAnswers: this.removeNosAndNaAnswers,
          documentsToInclude: this.exportableDocuments
            .filter(doc => !this.excluded.includes(doc.id))
            .map(doc => ({
              id: doc.id,
              path: doc.source,
              name: doc.name,
            })),
          saveAsName: this.fileNameToSave,
          saveAsNames: this.fileNamesToSave,
          zipped: true,
          triggerWebhook: this.exportToPlatforms.length > 0,
          convertImagesToPdfs: this.convertImagesToPdfs,
          exportToPlatforms: this.exportToPlatforms,
        });

        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              "Export queued! You will get a notifications once they're ready to be downloaded.",
          });
          this.dialogOpened = false;
          if (
            this.selectedQuestionnaires.length === 1 &&
            !this.selectedQuestionnaires[0].locked
          ) {
            this.$emit('open-lock-questionnaire-confirmation-dialog');
          }
        }

        this.dialogOpened = false;
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem exporting the questionnaire.',
        });
      }
    },
    async exportToScanAndAutoFlow(questionnaires) {
      try {
        const response1 = await API.questionnaire.exportQuestionnaires({
          questionnaireIds: questionnaires.map(q => q.id),
          documentsToInclude: this.exportableDocuments
            .filter(doc => !this.excluded.includes(doc.id))
            .map(doc => ({
              id: doc.id,
              path: doc.source,
              name: doc.name,
            })),
          saveAsName: this.fileNameToSave,
          saveAsNames: this.fileNamesToSave,
          format: 'Scan and AutoFlow',
        });

        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              "Export queued! You will get a notifications once they're ready to be downloaded.",
          });
          this.dialogOpened = false;
          if (
            this.selectedQuestionnaires.length === 1 &&
            !this.selectedQuestionnaires[0].locked
          ) {
            this.$emit('open-lock-questionnaire-confirmation-dialog');
          }
        }

        this.dialogOpened = false;
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem exporting the questionnaire.',
        });
      }
    },

    async exportToSpreadsheet(questionnaires) {
      for (const questionnaire of questionnaires) {
        const response = await API.questionnaire.exportQuestionnaireToSpreadsheet(
          {
            id: questionnaire.id,
            companyId: this.currentCompany.id,
          },
        );

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.saveAs(response.data, `${this.fileNameToSave}.xls`);
          this.dialogOpened = false;
          if (questionnaires.length === 1 && !questionnaires[0].locked) {
            this.$emit('open-lock-questionnaire-confirmation-dialog');
          }
        }
      }
    },

    async previewDocument(document) {
      try {
        this.selectedDocument = document;
        this.previewLoaded = false;
        this.previewType = this.endsWithAny(
          this.supportedExtensionsForPreview,
          document.source,
        );
        this.previewDocumentDialogOpened = true;

        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: document.source,
        });
        if (response.data.document) {
          if (this.previewType === 'heic') {
            this.documentPreviewData = await this.convertToJPEG(
              response.data.document.data.link,
            );
          } else {
            this.documentPreviewData = response.data.document.data.link;
            console.log(this.documentPreviewData);
          }
          this.previewLoaded = true;
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },

    async convertToJPEG(url) {
      const output = await fetch(url).then(async data => {
        const buffer = Buffer.from(await data.arrayBuffer());
        return this.heicConvert({ buffer, format: 'JPEG' });
      });
      const imgBase64 = btoa(
        output.reduce(
          (data, byte) => `${data}${String.fromCharCode(byte)}`,
          '',
        ),
      );
      return `data:image/png;base64,${imgBase64}`;
    },

    refresh() {
      this.loadQuestionnaire(
        this.selectedQuestionnaires[0],
        this.currentCompany.id,
      );
    },
    mapQuestionnairesToRecord(questionnaires) {
      if (!questionnaires || !questionnaires.length) return;

      const fileNameMapping = {};

      questionnaires.map(questionnaire => {
        const fileNameToSave = this.getFileNameFromQuestionnaire(questionnaire);

        fileNameMapping[questionnaire.id] = fileNameToSave;
      });

      return fileNameMapping;
    },

    initialize() {
      this.documents = [];
      this.selectedFormat = null;
      if (
        this.selectedQuestionnaires &&
        this.selectedQuestionnaires.length === 1
      ) {
        this.loadQuestionnaire(
          this.selectedQuestionnaires[0],
          this.currentCompany.id,
        );
      }

      this.fileNameToSave = this.getFileNameToSave();

      if (
        this.currentFirm.preferences.export_to_platforms &&
        this.currentFirm.preferences.export_to_platforms.length > 0
      ) {
        this.selectablePlatforms = this.currentFirm.preferences.export_to_platforms.map(
          platform => {
            return {
              name: platform,
              selected: false,
            };
          },
        );
      } else {
        this.selectablePlatforms = [
          {
            name: 'Third-party DMS',
            selected: false,
          },
        ];
      }
    },

    toggleExclude(docId) {
      if (this.excluded.includes(docId)) {
        this.excluded = this.excluded.filter(id => id !== docId);
      } else {
        this.excluded.push(docId);
      }
    },

    startEditDocDescription(doc) {
      doc.editingDescription = true;
      doc.newDescription = doc.description;
    },

    async saveNewDescription(doc) {
      const oldDescription = doc.description;

      try {
        doc.description = doc.newDescription;
        doc.editingDescription = false;

        const response = await API.extractedDocument.updateDescription({
          companyId: doc.company_id,
          extractedDocumentId: doc.id,
          description: doc.description,
        });
      } catch (error) {
        doc.description = oldDescription;
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'There was a problem updating the description of the document. Please try again!',
        });
      }
    },

    cancelEditingDescription(doc) {
      doc.editingDescription = false;
    },

    // Return the order of the document based on its description
    getDocumentOrder(doc) {
      let index = -1;

      // Put unknown documents at the end
      if (
        isEmpty(doc.description) ||
        doc.description.startsWith('Unknown Document')
      ) {
        index = 1000;
      } else {
        index = usTaxForms.findIndex(form => {
          return doc.description.startsWith(form);
        });

        // Put misc documents at the second last
        if (index === -1) {
          index = 999;
        }
      }

      return index;
    },

    async areTargetSheetsUploaded() {
      const response = await API.integration.getUploadedTargetSheets();

      const singlePage = response.data.find(
        integration =>
          integration.integration_data.document_type === 'single-page',
      );
      const multiPage = response.data.find(
        integration =>
          integration.integration_data.document_type === 'multi-page',
      );

      return singlePage && multiPage;
    },
  },

  watch: {
    selectedQuestionnaires(newQuestionnaires) {
      this.fileNamesToSave = this.mapQuestionnairesToRecord(newQuestionnaires);
    },
    fileNameToSave(newValue) {
      if (
        !this.selectedQuestionnaires ||
        !this.selectedQuestionnaires.length ||
        !this.selectedQuestionnaires[0] ||
        !this.selectedQuestionnaires[0].id
      )
        return;
      const firstQuestionnaireId = this.selectedQuestionnaires[0].id;

      this.fileNamesToSave[firstQuestionnaireId] = newValue;
    },
    value(newVal) {
      this.dialogOpened = newVal;

      if (newVal) {
        this.initialize();
      }
    },
    dialogOpened(newVal) {
      this.$emit('input', newVal);
      this.getExportTask();
    },
    async documents(newDocuments) {
      const seenSources = new Set();

      this.exportableDocuments = newDocuments
        .filter(d => {
          const isUniqueSource = !seenSources.has(d.source);
          if (isUniqueSource) seenSources.add(d.source);

          return (
            isUniqueSource &&
            !isEmpty(d.source) &&
            d.source !== 'Questionnaire' &&
            d.source !== 'Imported' &&
            // Do not include bulk documents that have been split
            d.status !== 'split_succeeded' &&
            // Do not include child documents that have not been reviewed by firm users
            (d.parent_document_id === null ||
              !['review_required', 'action_required'].includes(d.status))
          );
        })
        .map(d => {
          return {
            ...d,
            editingDescription: false,
          };
        })
        .sort((a, b) => {
          return this.getDocumentOrder(a) - this.getDocumentOrder(b);
        });
    },
  },
  async created() {
    this.dialogOpened = this.value;
    this.includeTOC = false;
    this.includePages = true;
    this.initialize();

    if (
      this.scanAndAutoFlowExportEnabled &&
      (await this.areTargetSheetsUploaded())
    ) {
      this.exportFormats.push('Scan and AutoFlow');
    }
  },
};
</script>

<style scoped>
.document_item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  background-color: white;
}

.document_item_name {
  cursor: pointer;
}

.document_item_name:hover {
  text-decoration: underline;
}
</style>
